import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { CloseDayDto, CustomerFilter, CustomerRowDto } from '@tiffinify/contracts';
import { formatDay, formatShort, waLink } from '@tiffinify/core';
import { Icon, LoadError, PageLoader } from '../components/ui';
import { Amt, Av, Back, Badge, Btn, Btn2, Card, Chip, Chips, Empty, Field, G2, Group, Hero, IB, Kick, Note, Row, Screen, Sm, Sp, Title, Top } from '../components/p';
import { del, post } from '../lib/api';
import { errorMessage, formatInr } from '../lib/format';
import { useCloseDay, useCustomers, useMe } from '../lib/queries';

/** Close the day (P42): what came in, who holds the cash, what was spent, then close it. */
export function CloseTheDay() {
  const qc = useQueryClient();
  const q = useCloseDay();
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const set = (d: CloseDayDto) => qc.setQueryData(['close-day', 'today'], d);
  const add = useMutation({ mutationFn: () => post<CloseDayDto>('/expenses', { day: q.data!.day, label, amount: Number(amount) || 0 }), onSuccess: (d) => { set(d); setLabel(''); setAmount(''); } });
  const remove = useMutation({ mutationFn: (id: string) => del<CloseDayDto>(`/expenses/${id}`), onSuccess: set });
  const close = useMutation({ mutationFn: () => post<CloseDayDto>('/close-day', { day: q.data!.day }), onSuccess: set });
  const reopen = useMutation({ mutationFn: () => del<CloseDayDto>(`/close-day/${q.data!.day}`), onSuccess: set });
  if (q.isError && !q.data) return <Screen><Top><Back to="/more" label="More" /></Top><LoadError what="today’s money" onRetry={() => void q.refetch()} /></Screen>;
  if (!q.data) return <Screen><Top><Back to="/more" label="More" /></Top><PageLoader label="Adding up the day" /></Screen>;
  const d = q.data;
  const err = add.error ?? remove.error ?? close.error ?? reopen.error;
  const closedAt = d.closed ? new Date(d.closed.at).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '';

  return (
    <Screen gap={16}>
      <Top><Back to="/more" label="More" /></Top>
      <Title h="Close the day" sub={`${formatDay(d.day)} · check the money, then close the day.`} />
      <Hero label="Taken today">
        <Kick>Taken {d.day === d.today ? 'today' : `on ${formatDay(d.day)}`}</Kick>
        <span className="p-big" style={{ fontSize: 54 }}>{formatInr(d.sales.total)}</span>
        <div className="p-facts"><div><Kick>Cash</Kick><b>{formatInr(d.sales.cash)}</b></div><div><Kick>UPI</Kick><b>{formatInr(d.sales.upi)}</b></div></div>
      </Hero>
      {(d.cashByPerson.length > 0 || d.kitchenCash > 0) && (
        <Group label="Cash with your team">
          <Card>
            {d.cashByPerson.map((p) => <Row key={p.name} left={<Av name={p.name} />} t={p.name} s={`${p.orders} ${p.orders === 1 ? 'order' : 'orders'} paid in cash`} right={<Amt>{formatInr(p.cash)}</Amt>} />)}
            {d.kitchenCash > 0 && <Row left={<Ic2 />} t="At the kitchen" s="Plans and extras paid in cash" right={<Amt>{formatInr(d.kitchenCash)}</Amt>} />}
          </Card>
        </Group>
      )}
      <Group label="Spent today (optional)">
        {d.expenses.length > 0 && (
          <Card>
            {d.expenses.map((e) => <Row key={e.id} left={<span className="p-ic" aria-hidden="true"><Icon name="box" size={17} /></span>} t={e.label}
              right={<span className="p-pair"><Amt>{formatInr(e.amount)}</Amt>{!d.closed && <Sm label="Remove" onClick={() => remove.mutate(e.id)} />}</span>} />)}
          </Card>
        )}
        {!d.closed && (
          <G2>
            <Field label="What for" id="x-label" value={label} placeholder="Vegetables and milk" onChange={setLabel} />
            <Field label="Amount ₹" id="x-amount" value={amount} inputMode="numeric" placeholder="0" onChange={(v) => setAmount(v.replace(/\D/g, ''))} />
          </G2>
        )}
        {!d.closed && <Btn2 label={add.isPending ? 'Adding…' : 'Add spending'} icon="plus" disabled={!label.trim() || !amount || add.isPending} onClick={() => add.mutate()} />}
      </Group>
      {d.saysPaid.count > 0 && <Note>{d.saysPaid.count} {d.saysPaid.count === 1 ? 'customer says they' : 'customers say they'} paid by UPI ({formatInr(d.saysPaid.amount)}). Check your bank app before you close.</Note>}
      {err && <span className="p-error" role="alert">{errorMessage(err)}</span>}
      {d.closed ? (
        <>
          <Note icon="check">Closed at {closedAt} by {d.closed.by}: {formatInr(d.closed.sales)} taken, {formatInr(d.closed.spent)} spent.</Note>
          <Btn2 label="Open the day again" onClick={() => reopen.mutate()} />
        </>
      ) : (
        <Btn label={`Close ${formatDay(d.day)} · profit ${formatInr(d.profit)}`} go="check" busy={close.isPending} onClick={() => close.mutate()} />
      )}
    </Screen>
  );
}

const Ic2 = () => <span className="p-ic s" aria-hidden="true"><Icon name="pot" size={17} /></span>;

const BADGE: Record<NonNullable<CustomerRowDto['badge']>, { label: string; tone: '' | 's' | 'g' | 'r' }> = {
  plan: { label: 'Plan', tone: 'g' }, offer: { label: 'Offer a plan', tone: 's' }, miss: { label: 'We miss you', tone: 'r' }, lately: { label: 'Not lately', tone: '' }, orders: { label: 'Orders', tone: '' },
};

/** All customers (P43): plans and orders in one list, with a nudge for each kind. */
export function AllCustomers() {
  const [filter, setFilter] = useState<CustomerFilter>('all');
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const kitchen = useMe().data?.kitchen.name ?? 'the kitchen';
  const list = useCustomers(filter, q.trim());
  const d = list.data;
  const line = (c: CustomerRowDto) => (c.onPlan ? 'On a plan'
    : c.badge === 'offer' ? `${c.ordersThisMonth} orders this month`
    : c.badge === 'miss' && c.planEndedOn ? `Plan ended ${formatShort(c.planEndedOn)}`
    : c.lastOrderDay ? `Last order ${formatShort(c.lastOrderDay)}` : 'No plan or order yet');
  const nudge = (c: CustomerRowDto) => {
    const first = c.name.trim().split(/\s+/)[0] ?? c.name;
    if (c.badge === 'offer') return <Sm label="Offer a plan" y to={`/s/${c.subscriberId}/renew`} />;
    if (c.badge === 'miss' || c.badge === 'lately') return <Sm label="We miss you" href={waLink(c.phone, `Hi ${first}, we miss you at ${kitchen}! Shall we send your lunch again this week?`)} />;
    return c.badge ? <Badge tone={BADGE[c.badge].tone}>{BADGE[c.badge].label}</Badge> : undefined;
  };
  return (
    <Screen gap={16}>
      <Top><Back to="/more" label="More" /><Sp /><IB icon={searching ? 'close' : 'search'} label={searching ? 'Close search' : 'Search customers'} onClick={() => { setSearching(!searching); setQ(''); }} /></Top>
      <Title h="All customers" sub="Plans and orders in one list." />
      {searching && (
        <label className="p-search"><Icon name="search" size={18} sw={2} /><input aria-label="Search customers" value={q} placeholder="Name, phone or area" onChange={(e) => setQ(e.target.value)} /></label>
      )}
      <Chips label="Filter customers">
        {([['all', 'All'], ['plan', 'On a plan'], ['orders', 'Orders only'], ['lately', 'Not lately']] as const).map(([f, label]) => (
          <Chip key={f} on={filter === f} onClick={() => setFilter(f)}>{label}{d ? ` ${d.counts[f]}` : ''}</Chip>
        ))}
      </Chips>
      {list.isError && !d ? <LoadError what="your customers" onRetry={() => void list.refetch()} />
        : !d ? <PageLoader label="Finding your customers" />
        : d.items.length === 0 ? <Card><Empty title="No one here">Try another filter.</Empty></Card>
        : <Card>{d.items.map((c) => {
            const acts = c.badge === 'offer' || c.badge === 'miss' || c.badge === 'lately';
            const s = [line(c), c.area].filter(Boolean).join(' · ');
            // A row with its own button can't also be one big link: then only the name opens the profile.
            return acts
              ? <Row key={c.subscriberId} left={<Av name={c.name} />} t={<Link to={`/s/${c.subscriberId}`} style={{ color: 'inherit', textDecoration: 'none' }}>{c.name}</Link>} s={s} right={nudge(c)} />
              : <Row key={c.subscriberId} to={`/s/${c.subscriberId}`} left={<Av name={c.name} />} t={c.name} s={s} right={nudge(c)} />;
          })}</Card>}
      <Note icon="chat">“Not lately” lists people who stopped ordering, each with a ready “We miss you” message.</Note>
    </Screen>
  );
}
